import type {
  FinishEventSurface,
  FinishesSource,
  FinishesView,
  FinishSheetSurface,
  RoomSettingProp,
} from "./events";
import type { Design } from "./types";
import { colorId, colorName, nearestColorName, type FinishOption } from "./finishes/catalog";
import { effectiveFinishes, inferredFinishes } from "./finishes/effective";
import { setUserProperty, subscribeTrackedEvents, track } from "./telemetry";

/**
 * Analytics for room finishes. The sheet reports what people do through
 * these helpers so every event carries the same `source` / `view` and the
 * sheet session can close with one summary (`finishes_closed`), the way
 * `render_3d_closed` summarizes a walk-through.
 */

/** `done`: the Done button; `dismiss`: close / Escape / swipe; `back`: the 3D view closed; `view_change`: the user moved on. */
export type FinishesCloseMethod = "done" | "dismiss" | "back" | "view_change";
export type FinishesDepth = "look" | "surface" | "wall";

interface Session {
  id: number;
  source: FinishesSource;
  view: FinishesView;
  openedAt: number;
  changes: number;
  surfaces: Set<string>;
  looks: Set<string>;
  types: Set<string>;
  colors: Set<string>;
  /** Tiles looked at and not picked (yet), reported when the sheet closes. */
  previewed: Map<string, { surface: FinishSheetSurface; type: string }>;
  undo: number;
  lastLook: string | null;
  unsubscribe: () => void;
}

let session: Session | null = null;
/** Finish changes this page session, for `finishes_saved_room`. */
let pageChanges = 0;
let savedReported = false;
/** Rooms whose first finish change this page session was reported. */
const sharedReported = new Set<string>();
const entriesShown = new Set<string>();
/** The change the 3D view is applying, for `finish_render_applied`. */
let pendingRender: { surface: FinishEventSurface; type: string } | null = null;
let installed = false;

const DEPTH_KEY = "room-canvas:finishes-depth";
/** Unpicked previews reported per sheet session, at most. */
const MAX_PREVIEWS = 8;
const DEPTH_RANK: Record<FinishesDepth, number> = { look: 1, surface: 2, wall: 3 };

function setting(design: Pick<Design, "room">): RoomSettingProp {
  return design.room.finishes?.setting === "outdoor" ? "outdoor" : "indoor";
}

function context() {
  return session ? { source: session.source, view: session.view } : null;
}

/** Deepest level of the sheet ever used on this device (a user property). */
function noteDepth(depth: FinishesDepth) {
  try {
    const prev = localStorage.getItem(DEPTH_KEY) as FinishesDepth | null;
    if (prev && DEPTH_RANK[prev] >= DEPTH_RANK[depth]) return;
    localStorage.setItem(DEPTH_KEY, depth);
  } catch {
    /* private mode: report anyway */
  }
  setUserProperty("finishes_depth", depth);
}

function noteChange(design: Pick<Design, "room">, sharedRoomId: string | null) {
  pageChanges++;
  if (session) session.changes++;
  if (pageChanges === 1) setUserProperty("used_finishes", true);
  const roomId = design.room.id;
  if (!sharedReported.has(roomId)) {
    sharedReported.add(roomId);
    track("finish_shared_room", { shared: sharedRoomId !== null });
  }
}

/** Saves after a finish change, once per page session. Call once at startup. */
export function installFinishesTelemetry(): () => void {
  if (installed) return () => {};
  installed = true;
  const off = subscribeTrackedEvents((name) => {
    if (name !== "room_saved" || savedReported || pageChanges === 0) return;
    savedReported = true;
    track("finishes_saved_room", { changes: pageChanges });
  });
  return () => {
    off();
    installed = false;
  };
}

/** An entry point to the sheet is on screen: once per page session per door. */
export function reportFinishesEntryShown(source: FinishesSource, view: FinishesView): void {
  if (entriesShown.has(source)) return;
  entriesShown.add(source);
  track("finishes_entry_shown", { source, view });
}

export function beginFinishesSession(opts: {
  id: number;
  source: FinishesSource;
  view: FinishesView;
  surface: FinishSheetSurface | null;
  wallKey: string | null;
  design: Pick<Design, "room" | "furniture">;
}): void {
  if (session?.id === opts.id) return;
  session?.unsubscribe();
  const s: Session = {
    id: opts.id,
    source: opts.source,
    view: opts.view,
    openedAt: performance.now(),
    changes: 0,
    surfaces: new Set(),
    looks: new Set(),
    types: new Set(),
    colors: new Set(),
    previewed: new Map(),
    undo: 0,
    lastLook: opts.design.room.finishes?.look ?? null,
    unsubscribe: () => {},
  };
  s.unsubscribe = subscribeTrackedEvents((name) => {
    if (name === "finish_undo") s.undo++;
  });
  session = s;
  const eff = effectiveFinishes(opts.design);
  setUserProperty("room_setting", eff.setting);
  track("finishes_opened", {
    source: opts.source,
    view: opts.view,
    setting: eff.setting,
    has_custom_finishes: eff.custom,
    surface: opts.surface,
    wall_key: opts.wallKey,
  });
}

export function endFinishesSession(method: FinishesCloseMethod, design: Pick<Design, "room">): void {
  const s = session;
  if (!s) return;
  session = null;
  s.unsubscribe();
  const look = design.room.finishes?.look ?? null;
  for (const p of [...s.previewed.values()].slice(0, MAX_PREVIEWS)) {
    track("finish_option_previewed", { ...p, source: s.source, view: s.view });
  }
  track("finishes_closed", {
    source: s.source,
    view: s.view,
    setting: setting(design),
    duration_ms: Math.round(performance.now() - s.openedAt),
    changes: s.changes,
    surfaces_touched: s.surfaces.size,
    looks_tried: s.looks.size,
    types_tried: s.types.size,
    colors_tried: s.colors.size,
    undo_count: s.undo,
    ended_with_look: s.lastLook !== null && look === s.lastLook,
    method,
  });
}

/** Finish changes this page session (the 3D view reports its own share on close). */
export function finishChangeCount(): number {
  return pageChanges;
}

/**
 * The core event. `before` is the design before the change (for the
 * Look-customized and default-type checks).
 */
export function trackFinishChange(opts: {
  surface: FinishEventSurface;
  option: FinishOption;
  color: string;
  changed: "type" | "color" | "both";
  before: Pick<Design, "room" | "furniture">;
  sharedRoomId: string | null;
}): void {
  const ctx = context();
  if (!ctx) return;
  const { surface, option, color, changed, before } = opts;
  const eff = effectiveFinishes(before);
  const sheetSurface = surface === "wall" ? "walls" : surface === "trim" ? "walls" : surface;
  const inferred = inferredFinishes(before, eff.setting);
  const inferredType = sheetSurface === "floor" ? inferred.floor.type : sheetSurface === "walls" ? inferred.walls.type : inferred.ceiling.type;
  const id = colorId(option, color);
  track("finish_changed", {
    surface,
    type: option.id,
    color: id,
    color_name: colorName(option, color),
    scope: surface === "wall" ? "wall" : "room",
    setting: eff.setting,
    ...ctx,
    is_default_type: option.id === inferredType,
    changed,
  });
  if (eff.look) track("look_customized", { look: eff.look.id, surface, ...ctx });
  if (session) {
    session.surfaces.add(surface);
    session.types.add(`${sheetSurface}:${option.id}`);
    session.colors.add(`${surface}:${color.toLowerCase()}`);
    session.previewed.delete(`${sheetSurface}:${option.id}`);
  }
  noteDepth(surface === "wall" ? "wall" : "surface");
  noteChange(before, opts.sharedRoomId);
  pendingRender = { surface, type: option.id };
}

export function trackLookApplied(opts: { look: string; before: Pick<Design, "room" | "furniture">; sharedRoomId: string | null }): void {
  const ctx = context();
  if (!ctx) return;
  const eff = effectiveFinishes(opts.before);
  track("look_applied", { look: opts.look, ...ctx, replaced_custom: eff.custom && !eff.look });
  if (session) {
    session.looks.add(opts.look);
    session.lastLook = opts.look;
  }
  noteDepth("look");
  noteChange(opts.before, opts.sharedRoomId);
  pendingRender = { surface: "floor", type: `look:${opts.look}` };
}

export function trackTrimChange(opts: { color: string; before: Pick<Design, "room" | "furniture">; sharedRoomId: string | null }): void {
  const ctx = context();
  if (!ctx) return;
  const name = nearestColorName(opts.color);
  track("finish_trim_color_changed", { color: opts.color.toLowerCase(), color_name: name, ...ctx });
  const eff = effectiveFinishes(opts.before);
  if (eff.look) track("look_customized", { look: eff.look.id, surface: "trim", ...ctx });
  if (session) {
    session.surfaces.add("trim");
    session.colors.add(`trim:${opts.color.toLowerCase()}`);
  }
  noteDepth("surface");
  noteChange(opts.before, opts.sharedRoomId);
  pendingRender = { surface: "trim", type: "trim" };
}

export function trackDirectionChange(direction: "auto" | "across", type: string, before: Pick<Design, "room">, sharedRoomId: string | null): void {
  const ctx = context();
  if (!ctx) return;
  track("finish_direction_changed", { direction, type, ...ctx });
  if (session) session.surfaces.add("floor");
  noteDepth("surface");
  noteChange(before, sharedRoomId);
  pendingRender = { surface: "floor", type };
}

/**
 * A tile was hovered (desktop) or pressed and held (touch). Reported when
 * the sheet closes, and only if it was never picked: the attention it got
 * without winning.
 */
export function noteFinishPreview(surface: FinishSheetSurface, type: string, current: boolean): void {
  if (!session || current) return;
  const key = `${surface}:${type}`;
  if (session.types.has(key)) return;
  session.previewed.set(key, { surface, type });
}

export function trackScopeChange(scope: "room" | "wall"): void {
  const ctx = context();
  if (ctx) track("finishes_scope_changed", { scope, ...ctx });
}

export function trackOverrideSet(type: string, wallKey: string): void {
  const ctx = context();
  if (ctx) track("wall_finish_override_set", { type, wall_key: wallKey, ...ctx });
}

/** Outdoor: a boundary changed type. `wallKey` null means every side at once ("all"). */
export function trackBoundaryChange(type: string, wallKey: string | null): void {
  const ctx = context();
  if (ctx) track("boundary_changed", { type, wall_key: wallKey ?? "all", ...ctx });
}

export function trackOverrideCleared(method: "chip" | "reset_all", before: Pick<Design, "room">, sharedRoomId: string | null): void {
  const ctx = context();
  if (!ctx) return;
  track("wall_finish_override_cleared", { method, ...ctx });
  if (session) session.surfaces.add("wall");
  noteChange(before, sharedRoomId);
  pendingRender = { surface: "wall", type: "reset" };
}

/**
 * Indoor / Outdoor. From the sheet the session supplies `source` / `view`;
 * Room details passes its own `from` since no sheet is open there.
 */
export function trackRoomSettingChange(opts: {
  to: RoomSettingProp;
  before: Pick<Design, "room">;
  sharedRoomId: string | null;
  from?: { source: FinishesSource; view: FinishesView };
}): void {
  const ctx = context() ?? opts.from ?? null;
  if (!ctx) return;
  const saved = opts.before.room.finishes;
  track("room_setting_changed", {
    from: setting(opts.before),
    to: opts.to,
    ...ctx,
    had_ceiling_finish: !!saved?.ceiling,
    had_wall_overrides: Object.keys(saved?.wallOverrides ?? {}).length > 0,
  });
  setUserProperty("room_setting", opts.to);
  noteChange(opts.before, opts.sharedRoomId);
  pendingRender = { surface: "floor", type: `setting:${opts.to}` };
}

export function trackFinishesReset(surface: FinishSheetSurface | "all", before: Pick<Design, "room">, sharedRoomId: string | null): void {
  const ctx = context();
  if (!ctx) return;
  track("finishes_reset", { surface, ...ctx });
  if (session) session.surfaces.add(surface === "all" ? "floor" : surface);
  noteChange(before, sharedRoomId);
  pendingRender = { surface: surface === "all" ? "floor" : surface, type: "reset" };
}

/** The change the 3D view just put on screen (once). */
export function takePendingFinishRender(): { surface: FinishEventSurface; type: string } | null {
  const p = pendingRender;
  pendingRender = null;
  return p;
}

// --- Coach tip --------------------------------------------------------------

const TIP_KEY = "room-canvas:finishes-tip";

export function finishesTipSeen(): boolean {
  try {
    return localStorage.getItem(TIP_KEY) === "1";
  } catch {
    return true;
  }
}

export function markFinishesTipSeen(): void {
  try {
    localStorage.setItem(TIP_KEY, "1");
  } catch {
    /* shows again next time; harmless */
  }
}

/** Test hook. */
export function _resetFinishesTelemetryForTests(): void {
  session?.unsubscribe();
  session = null;
  pageChanges = 0;
  savedReported = false;
  sharedReported.clear();
  entriesShown.clear();
  pendingRender = null;
}
